/*
  url-filter-controller.js

  Generic URL-state filter controller. Reads any element marked with
  data-filter-tile, treats its data-filter-param and data-filter-value
  as the URL query param to toggle, updates window.location via
  history.pushState, fetches the target Shopify section via the
  Section Rendering API, and swaps it into the DOM in place.

  No knowledge of specific filter types. Works for tag filters today,
  will work for vendor / metafield / sort / pagination later with
  zero changes.

  IIFE pattern matches theme.js.
*/

(function () {
  'use strict';

  const URL_FILTER_EVENT = 'urlfilter:change';

  let isFetching = false;

  /* Find the grid section's Shopify wrapper by walking up from .c-plp-grid.
     We cannot hardcode the section id because Shopify renders JSON-template
     sections with a generated prefix (template--<theme_id>__<section_key>)
     that varies per environment. */
  function getGridSectionElement() {
    const grid = document.querySelector('.c-plp-grid');
    if (!grid) return null;
    return grid.closest('[id^="shopify-section-"]');
  }

  function getGridSectionId() {
    const sectionEl = getGridSectionElement();
    if (!sectionEl) return null;
    return sectionEl.id.replace(/^shopify-section-/, '');
  }

  function getCurrentUrl() {
    return new URL(window.location.href);
  }

  function buildToggledUrl(paramName, value, mode) {
    const url = getCurrentUrl();
    const currentValues = url.searchParams.getAll(paramName);
    const alreadyOn = currentValues.indexOf(value) !== -1;
    // Single-select facets (Activity, Market) replace the param's value on
    // click and clear it when re-clicking the active value. Multi-select
    // (default) appends or removes a single value from the existing list.
    let newValues;
    if (mode === 'single') {
      newValues = alreadyOn ? [] : [value];
    } else {
      newValues = alreadyOn
        ? currentValues.filter(function (existing) { return existing !== value; })
        : currentValues.concat(value);
    }

    url.searchParams.delete(paramName);
    newValues.forEach(function (v) {
      url.searchParams.append(paramName, v);
    });
    return url;
  }

  function refetchGrid() {
    const sectionId = getGridSectionId();
    if (!sectionId) {
      return Promise.reject(new Error('Grid section not found in DOM'));
    }
    const url = getCurrentUrl();
    url.searchParams.set('section_id', sectionId);
    return fetch(url.toString(), {
      headers: { 'Accept': 'text/html' }
    }).then(function (response) {
      if (!response.ok) {
        throw new Error('Section fetch failed: ' + response.status);
      }
      return response.text();
    });
  }

  function swapGrid(html) {
    /* Ownership can flip while this fetch is in flight. Re-check before the
       destructive write or a late native swap clobbers the Searchanise render. */
    if (window.searchanisePlpOwns) return false;

    const target = getGridSectionElement();
    if (!target) {
      throw new Error('Grid section target not found in DOM');
    }
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = html.trim();
    const replacement = tempContainer.firstElementChild;
    if (!replacement) {
      throw new Error('Section response had no root element');
    }
    // Suppress transitions for two frames after swap so no animation fires
    // on freshly mounted elements (otherwise <details>, opacity, transform
    // rules can briefly fire as the new section initializes).
    replacement.classList.add('is-rerendering');
    target.replaceWith(replacement);
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        replacement.classList.remove('is-rerendering');
      });
    });
    return true;
  }

  function setGridLoading(loading) {
    const grid = getGridSectionElement();
    if (!grid) return;
    if (typeof window.toggleData === 'function') {
      window.toggleData(grid, 'loading', loading);
    } else {
      grid.dataset.loading = loading ? 'true' : 'false';
    }
  }

  function refreshTileSelectedStates() {
    const url = getCurrentUrl();
    const tiles = document.querySelectorAll('[data-filter-tile]');
    tiles.forEach(function (tile) {
      const paramName = tile.getAttribute('data-filter-param');
      const value = tile.getAttribute('data-filter-value');
      const selected = url.searchParams.getAll(paramName).indexOf(value) !== -1;
      tile.setAttribute('data-selected', selected ? 'true' : 'false');
      tile.setAttribute('aria-pressed', selected ? 'true' : 'false');
    });
  }

  function broadcastChange() {
    document.dispatchEvent(new CustomEvent(URL_FILTER_EVENT));
  }

  function refreshGrid() {
    setGridLoading(true);
    return refetchGrid()
      .then(function (html) {
        /* No swap means ownership flipped mid-fetch, so there is no new DOM for
           listeners to react to. */
        if (swapGrid(html)) broadcastChange();
      })
      .catch(function (error) {
        console.error('[url-filter-controller] Refresh failed:', error);
      })
      .then(function () {
        setGridLoading(false);
      });
  }

  function applyFilterToggle(paramName, value, mode) {
    if (isFetching) return;
    isFetching = true;

    const newUrl = buildToggledUrl(paramName, value, mode);
    window.history.pushState({}, '', newUrl);
    refreshTileSelectedStates();

    refreshGrid().then(function () {
      isFetching = false;
      scrollToGridTop();
    });
  }

  function applyUrlNav(href) {
    if (isFetching) return;
    isFetching = true;
    window.history.pushState({}, '', href);
    refreshTileSelectedStates();
    refreshGrid().then(function () {
      isFetching = false;
      scrollToGridTop();
    });
  }

  function scrollToGridTop() {
    const target = document.querySelector('.c-product-list-page');
    if (!target) return;
    /* Skip when the mobile filter drawer is open — the user is mid-selection
       inside a fixed-position drawer; scrolling the page under it is
       disorienting and fights the drawer's own facet-list scroll. */
    if (target.dataset.filterOpen === 'true') return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function handlePopState() {
    if (window.searchanisePlpOwns) return;
    if (isFetching) return;
    isFetching = true;

    refreshTileSelectedStates();
    refreshGrid().then(function () {
      isFetching = false;
    });
  }

  function handleClick(event) {
    if (window.searchanisePlpOwns) return;

    // Pagination link: intercept and refetch the section instead of full nav.
    const pagerLink = event.target.closest('.c-pagination a[href]');
    if (pagerLink) {
      event.preventDefault();
      applyUrlNav(pagerLink.getAttribute('href'));
      return;
    }

    // Category tile: an in-market category (href targets the SAME collection)
    // refines in place via AJAX like a facet, so the page never reloads and the
    // entrance animations don't replay. A cross-market category (Brush / Straps,
    // whose href points at a DIFFERENT collection / all-products) falls through to
    // a normal full navigation, where the entrance animations are meant to play.
    const categoryLink = event.target.closest('[data-category-tile-link]');
    if (categoryLink) {
      const href = categoryLink.getAttribute('href');
      let url = null;
      if (href) {
        try { url = new URL(href, window.location.href); } catch { url = null; }
      }
      if (url && url.pathname === window.location.pathname) {
        event.preventDefault();
        applyUrlNav(href);
      }
      return; // different collection -> let the browser navigate
    }

    const tile = event.target.closest('[data-filter-tile]');
    if (!tile) return;
    event.preventDefault();
    const paramName = tile.getAttribute('data-filter-param');
    const value = tile.getAttribute('data-filter-value');
    const mode = tile.getAttribute('data-filter-mode') || 'multi';
    if (!paramName || !value) return;
    applyFilterToggle(paramName, value, mode);
  }

  function init() {
    document.addEventListener('click', handleClick);
    window.addEventListener('popstate', handlePopState);
    // Expose a no-arg refresh for non-tile callers (e.g. sort dropdown).
    window.urlFilterRefreshGrid = function () {
      if (isFetching) return Promise.resolve();
      isFetching = true;
      refreshTileSelectedStates();
      return refreshGrid().then(function () {
        isFetching = false;
        scrollToGridTop();
      });
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
